import React, { useState } from 'react';
import { findFirstDifferentCharacter, toFormattedPhoneNumber, toNumbersOnly } from '../../../utils';
import { InputText, type InputTextProps } from './InputText/InputText';

type Props = Omit<
  InputTextProps,
  | 'autoCapitalize'
  | 'autoComplete'
  | 'autoCorrect'
  | 'dataDetectorTypes'
  | 'keyboardType'
  | 'multiline'
  | 'numberOfLines'
  | 'onContentSizeChange'
  | 'optional'
  | 'passwordRules'
  | 'scrollEnabled'
  | 'secureTextEntry'
  | 'textContentType'
> & {
  isMaestro?: boolean;
};

const specialPhoneCharacters = [')', '-', ' '];

export const getNewPhoneDigitsOnChange = (previousFormattedNumber: string, newFormattedNumber: string) => {
  const numbersOnly = toNumbersOnly(newFormattedNumber);

  const oneCharacterDeleted = previousFormattedNumber.length - newFormattedNumber.length === 1;
  if (oneCharacterDeleted) {
    const { character: characterDeleted } = findFirstDifferentCharacter(newFormattedNumber, previousFormattedNumber);

    if (specialPhoneCharacters.includes(characterDeleted)) {
      switch (characterDeleted) {
        case ')':
        case ' ':
          return numbersOnly.slice(0, 2) + numbersOnly.slice(3, numbersOnly.length);
        case '-':
          return numbersOnly.slice(0, 5) + numbersOnly.slice(6, numbersOnly.length);
        default:
          break;
      }
    }
  }

  return numbersOnly;
};

export const InputPhone = ({
  isMaestro,
  accessibilityLabel,
  autoFocus,
  backgroundColor,
  blurOnSubmit,
  caption,
  defaultValue,
  disabled,
  error,
  hideClearButton,
  label,
  leftIconProps,
  onBlur,
  onChangeText,
  onFocus,
  onKeyPress,
  onRef,
  onSubmitEditing,
  placeholder,
  required,
  returnKeyType,
  rightIconProps,
  sublabel,
  testID,
  value,
}: Props) => {
  const [phoneDigits, setPhoneDigits] = useState<string>(toNumbersOnly(value));

  const handleChangeText = (newFormattedNumber: string) => {
    if (isMaestro) {
      onChangeText?.(newFormattedNumber);
      setPhoneDigits(newFormattedNumber);
      return;
    }

    const previousFormattedNumber = toFormattedPhoneNumber(phoneDigits);
    const newPhoneDigits = getNewPhoneDigitsOnChange(previousFormattedNumber, newFormattedNumber);

    setPhoneDigits(newPhoneDigits);

    if (onChangeText) {
      onChangeText(newPhoneDigits);
    }
  };

  return (
    <InputText
      accessibilityLabel={accessibilityLabel || 'Phone'}
      autoCapitalize="none"
      autoComplete="tel"
      autoCorrect={false}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={autoFocus}
      backgroundColor={backgroundColor}
      blurOnSubmit={blurOnSubmit}
      caption={caption}
      defaultValue={defaultValue}
      disabled={disabled}
      error={error}
      hideClearButton={hideClearButton}
      keyboardType="number-pad"
      label={label}
      leftIconProps={leftIconProps}
      // Set to length of "(xxx) xxx-xxxx" to stop flicker
      maxLength={14}
      onBlur={onBlur}
      onChangeText={handleChangeText}
      onFocus={onFocus}
      onKeyPress={onKeyPress}
      onRef={onRef}
      onSubmitEditing={onSubmitEditing}
      placeholder={placeholder || '(555) 555-5555'}
      required={required}
      returnKeyType={returnKeyType}
      rightIconProps={rightIconProps}
      sublabel={sublabel}
      testID={testID}
      textContentType="telephoneNumber"
      value={toFormattedPhoneNumber(phoneDigits, isMaestro)}
    />
  );
};
